// music/dispatch.jsx — pure dispatch helpers.
//
// Three dispatch shapes per BRIEF-music-card.md §6 Targets model:
//
//   dispatchToSpeaker(content, entityId)
//     Single Sonos target. Calls media_player.play_media with the
//     Spotify URI. Returns the entityId (which becomes the coordinator
//     for any subsequent group operations).
//
//   dispatchToGroup(content, presetKey, coordinatorEntity)
//     Named preset group (Whole house, Pavilion + Pool, etc.). Fires
//     the matching script.sonos_* helper to form the group, then
//     play_media on the coordinator. Returns coordinator entityId.
//
//   dispatchCustom(content, entityIds, coordinatorEntity)
//     Ad-hoc multi-speaker group. media_player.join the followers to
//     the coordinator, then play_media on the coordinator. Returns
//     coordinator entityId.
//
// All three return the coordinator's entityId so the caller (MusicShell)
// can map it back to a room and set focusRoomId for the post-dispatch
// hero refresh.
//
// Spotify URI shape verified end-to-end 2026-05-23 — see
// memory project-sonos-accepts-spotify-uris. Sonos accepts native
// spotify:track / :album / :playlist URIs via play_media with
// media_content_type: 'music'.

// Named-group preset → script helper. Per HANDOFF-ha-av.md §1.
const PRESET_HELPERS = {
  whole_house:       { script: 'sonos_group_pavilion',       coordinator: 'media_player.pavilion' },
  pavilion_pool:     { script: 'sonos_pool_pavilion_group',  coordinator: 'media_player.pavilion' },
  // 'currently_playing' and 'custom' are handled outside the preset path
  // because they don't have a single fixed coordinator.
};

// Pavilion + Rumpus are special: their "Sonos" is actually a Sonos Port /
// Connect feeding a line input on an external amp (DRX-3.1 / DRX-5). The
// stream reaches speakers only when the amp is in listening mode (main
// on, source CD, ALL CH STEREO). HA-side join/play_media leaves the amp
// untouched, so without the guard the UI says "playing" and the room is
// silent. Per HANDOFF-ha-av.md §1 + four-state Pavilion model in §4.
const AMP_LISTEN_SCRIPTS = {
  'media_player.pavilion': 'pavilion_listen',
  'media_player.rumpus':   'rumpus_listen',
};

export async function ensureAmpListening(hass, entityIds) {
  if (!hass?.callService) return;
  const ids = Array.isArray(entityIds) ? entityIds : [entityIds];
  const scripts = [...new Set(
    ids.filter(Boolean).map((id) => AMP_LISTEN_SCRIPTS[id]).filter(Boolean)
  )];
  if (scripts.length === 0) return;
  await Promise.all(scripts.map(async (script) => {
    try {
      await hass.callService('script', script, {});
    } catch (e) {
      console.warn(`[music/dispatch] amp-listen ${script} failed`, e);
    }
  }));
}

// Convert a tile-shaped content item into the Spotify URI Sonos accepts.
// Returns null for kinds we don't dispatch directly (artist — user
// drills into S2 and picks a track instead).
//
// Podcasts: Sonos S2 accepts spotify:episode:… (plays that episode) and
// spotify:show:… (Sonos picks the most-recent / continue-listening
// episode based on the Sonos account's Spotify history). Both go
// through the same media_player.play_media path with content_type
// 'music' — HA's Sonos integration passes the URI through unmodified.
export function spotifyUriFor(content) {
  if (!content || !content.id) return null;
  switch (content.kind) {
    case 'track':    return `spotify:track:${content.id}`;
    case 'album':    return `spotify:album:${content.id}`;
    case 'playlist': return `spotify:playlist:${content.id}`;
    case 'episode':  return `spotify:episode:${content.id}`;
    case 'show':     return `spotify:show:${content.id}`;
    case 'artist':   return null;  // dispatch a track from artist's top-tracks instead
    default:         return null;
  }
}

export function createDispatch(hass) {
  if (!hass?.callService) {
    return {
      dispatchToSpeaker: async () => null,
      dispatchToGroup:   async () => null,
      dispatchCustom:    async () => null,
      dispatchLineIn:    async () => null,
      dispatchExtend:    async () => null,
      queueTracks:       async () => {},
    };
  }

  const dispatchToSpeaker = async (content, entityId) => {
    if (!content || !entityId) {
      console.warn('[music/dispatch] dispatchToSpeaker — missing content or entity', { content, entityId });
      return null;
    }
    // Pavilion / Rumpus require their external amp in listening mode
    // before any audio is audible — guard fires the listen script if
    // the target is one of those Sonos endpoints. No-op for others.
    await ensureAmpListening(hass, entityId);
    // Apple Music library content (apple-playlist / apple-album) can't be played
    // by media_player.play_media — Sonos rejects a bare Apple container URI. Route
    // it through the ha-av apple_sonos.play service, which constructs the Sonos
    // music-service URI + metadata and queue-plays. The zone name is the target
    // entity's friendly_name (= Sonos zone). The caller's skipForward then advances
    // to opts.startIndex via media_next_track, exactly like Spotify.
    if (content.kind === 'apple-playlist' || content.kind === 'apple-album') {
      const zone = hass.states?.[entityId]?.attributes?.friendly_name || entityId;
      try {
        await hass.callService('apple_sonos', 'play', {
          room: zone,
          content_id: content.id,
          kind: content.kind === 'apple-album' ? 'album' : 'playlist',
          title: content.name || '',
        });
        return entityId;
      } catch (e) {
        console.error('[music/dispatch] apple_sonos.play failed', { entityId, id: content.id, error: e?.message || e });
        return null;
      }
    }
    // Sonos Favourites dispatch through HA's favorite_item_id content
    // type — the favourite's own media_content_id resolves natively on
    // the Sonos side (radio stream, Spotify URI, custom playlist, etc.
    // all handled by Sonos). Content type is preserved verbatim from
    // Sonos's browse_media response so non-standard favourite shapes
    // (if any) still route correctly.
    if (content.kind === 'favourite') {
      try {
        await hass.callService('media_player', 'play_media', {
          entity_id: entityId,
          media_content_type: content.contentType || 'favorite_item_id',
          media_content_id: content.id,
        });
        return entityId;
      } catch (e) {
        console.error('[music/dispatch] favourite play_media failed', { entityId, id: content.id, error: e?.message || e });
        return null;
      }
    }
    // Default path: Spotify URI dispatch (track/album/playlist/etc.).
    const uri = spotifyUriFor(content);
    if (!uri) {
      console.warn('[music/dispatch] dispatchToSpeaker — no URI for content', { content });
      return null;
    }
    try {
      await hass.callService('media_player', 'play_media', {
        entity_id: entityId,
        media_content_type: 'music',
        media_content_id: uri,
      });
      return entityId;
    } catch (e) {
      console.error('[music/dispatch] play_media failed', { entityId, uri, error: e?.message || e });
      return null;
    }
  };

  const dispatchToGroup = async (content, presetKey, overrideCoordinator) => {
    const preset = PRESET_HELPERS[presetKey];
    const coordinator = overrideCoordinator || preset?.coordinator;
    if (!preset || !coordinator) {
      console.warn('[music/dispatch] dispatchToGroup — unknown preset', { presetKey });
      return null;
    }
    try {
      // Form the group first via the household helper script. These
      // scripts handle the coordinator-setup + member-join sequence
      // we don't want to re-implement client-side.
      await hass.callService('script', preset.script, {});
      // Wait a brief tick so Sonos finishes the group reconfigure
      // before we push play_media. Without this we sometimes see
      // play_media land on the wrong speaker mid-group-formation.
      await new Promise((r) => setTimeout(r, 400));
      return await dispatchToSpeaker(content, coordinator);
    } catch (e) {
      console.error('[music/dispatch] dispatchToGroup failed', { presetKey, error: e?.message || e });
      return null;
    }
  };

  const dispatchCustom = async (content, entityIds, coordinatorEntity) => {
    if (!coordinatorEntity || !Array.isArray(entityIds) || entityIds.length === 0) {
      console.warn('[music/dispatch] dispatchCustom — missing inputs', { entityIds, coordinatorEntity });
      return null;
    }
    const followers = entityIds.filter((e) => e !== coordinatorEntity);
    try {
      // Amp-listen guard for any Pavilion/Rumpus members across the
      // group — both coordinator and followers count.
      await ensureAmpListening(hass, [coordinatorEntity, ...followers]);
      if (followers.length > 0) {
        await hass.callService('media_player', 'join', {
          entity_id: coordinatorEntity,
          group_members: followers,
        });
        // Same coordinator-settling tick as dispatchToGroup.
        await new Promise((r) => setTimeout(r, 400));
      }
      return await dispatchToSpeaker(content, coordinatorEntity);
    } catch (e) {
      console.error('[music/dispatch] dispatchCustom failed', { entityIds, error: e?.message || e });
      return null;
    }
  };

  // Line-in dispatch. The source speaker is the one with the physical
  // line-in jack (Sonos Port / Connect / Connect:Amp). Order matters:
  // select_source FIRST so the source starts producing line-in audio,
  // THEN join followers — otherwise followers wait silently while
  // their source changes under them. If the source is its own only
  // target (followers = []), the join step is skipped.
  //
  // Pavilion / Rumpus need their external amp in listening mode when
  // they appear as either source or follower. ensureAmpListening
  // handles both ends.
  const dispatchLineIn = async (sourceEntity, followerEntities = []) => {
    if (!sourceEntity) {
      console.warn('[music/dispatch] dispatchLineIn — missing source entity');
      return null;
    }
    const followers = (followerEntities || []).filter((e) => e && e !== sourceEntity);
    try {
      await ensureAmpListening(hass, [sourceEntity, ...followers]);
      await hass.callService('media_player', 'select_source', {
        entity_id: sourceEntity,
        source: 'Line-in',
      });
      if (followers.length > 0) {
        // Settle tick — same as dispatchToGroup / dispatchCustom. Without
        // this, the join occasionally lands before the source switch
        // finalises and the follower stays on its prior content.
        await new Promise((r) => setTimeout(r, 400));
        await hass.callService('media_player', 'join', {
          entity_id: sourceEntity,
          group_members: followers,
        });
      }
      return sourceEntity;
    } catch (e) {
      console.error('[music/dispatch] dispatchLineIn failed', { sourceEntity, followers, error: e?.message || e });
      return null;
    }
  };

  // "Send to other speakers" extend path. Joins targetEntities into
  // coordinatorEntity's existing group without dispatching any new
  // content — current playback continues on the coordinator and the
  // followers join in. Used by S4's "Send to other speakers" button.
  // Returns coordinator (caller updates view focus accordingly).
  const dispatchExtend = async (coordinatorEntity, targetEntities) => {
    if (!coordinatorEntity || !Array.isArray(targetEntities)) {
      console.warn('[music/dispatch] dispatchExtend — missing inputs', { coordinatorEntity, targetEntities });
      return null;
    }
    const followers = targetEntities.filter((e) => e && e !== coordinatorEntity);
    if (followers.length === 0) return coordinatorEntity;
    try {
      // Coordinator is already audible (playback was in progress before
      // extend), but a Pavilion/Rumpus follower needs its amp on before
      // it'll be audible after the join.
      await ensureAmpListening(hass, [coordinatorEntity, ...followers]);
      await hass.callService('media_player', 'join', {
        entity_id: coordinatorEntity,
        group_members: followers,
      });
      return coordinatorEntity;
    } catch (e) {
      console.error('[music/dispatch] dispatchExtend failed', { coordinatorEntity, followers, error: e?.message || e });
      return null;
    }
  };

  // Enqueue additional tracks after an initial dispatch — used by the
  // S2 artist view's "Play all" to queue the artist's remaining
  // tracks after the first one starts playing. media_player.play_media
  // with `enqueue: 'add'` appends to Sonos's queue without resetting
  // playback. Sequential awaits preserve track order; parallel calls
  // would risk Sonos shuffling them.
  //
  // Caller is responsible for having just dispatched the FIRST track
  // (which sets up the queue + starts playback). The settle delay
  // below is so Sonos finishes processing that first play_media
  // before we start appending — without it the first few enqueue
  // adds occasionally race against Sonos's queue reset.
  const queueTracks = async (coordinatorEntity, trackItems) => {
    if (!coordinatorEntity || !Array.isArray(trackItems) || trackItems.length === 0) return;
    if (!hass?.callService) return;
    await new Promise((r) => setTimeout(r, 600));
    for (const item of trackItems) {
      const uri = spotifyUriFor(item);
      if (!uri) continue;
      try {
        await hass.callService('media_player', 'play_media', {
          entity_id: coordinatorEntity,
          media_content_type: 'music',
          media_content_id: uri,
          enqueue: 'add',
        });
      } catch (e) {
        // Don't abort — keep trying the rest. Partial queue is better
        // than nothing if a single enqueue call is rejected.
        console.warn('[music/dispatch] queueTracks — enqueue failed', { uri, error: e?.message || e });
      }
    }
  };

  return { dispatchToSpeaker, dispatchToGroup, dispatchCustom, dispatchLineIn, dispatchExtend, queueTracks };
}

// Reverse-map a Sonos entity_id back to a room id for setting focusRoomId
// after dispatch. Mirrors the entity table in shell.jsx primaryEntityFor
// (Sonos-side only — amp entities aren't in this map).
const ENTITY_TO_ROOM = {
  'media_player.office':         'office',
  'media_player.pavilion':       'pavilion',
  'media_player.rumpus':         'rumpus',
  'media_player.master_bed':     'master_bed',
  'media_player.ensuite':        'master_ensuite',
  'media_player.pool':           'pool',
  // Desk + Office Turntable + standalones + portables don't map to a
  // panel room (they're not in ROOMS). MusicShell handles null gracefully
  // by falling back to findPlayingRoom or showing the brand mark.
};

export function roomIdForEntity(entity) {
  return ENTITY_TO_ROOM[entity] || null;
}
